import {UnitValue} from '../../../data-structures/unit-value.model';
import {InputSummaryItem} from '../../../input-summary';
import InputSummaryUtils from '../../../../helper/input-summary/input-summary-util';
import {ProjectValidationUnit} from '../../../data-structures/project-validation';
import { UnitValueValidation } from '../../../../helper/validation/validation-sub-modules/unit-value.validation';
import {v4 as uuid} from 'uuid';
import { AnessaListItem } from '../../../anessa-list-item.interface';


export interface PriceStructureInterface {
  _id: string;
  name: string;
  uuid: string;
  cpi: UnitValue;
  rangeFrom: number;
  rangeTo: number;
  rangeToMax: boolean;
  rangeUnit: UnitValue;
  rangePer: UnitValue;
  factorOf: number;
  price: UnitValue;
  pricePer: UnitValue;
  fromYear: number;
  toYear: number;
  toYearMax: boolean;
  inputsErrors: {};
}

export class PriceStructure implements AnessaListItem {

  public _id: string;
  public name: string;
  uuid: string;
  public cpi: UnitValue;
  public rangeFrom: number;
  public rangeTo: number;
  public rangeToMax: boolean;
  public rangeUnit: UnitValue;
  public rangePer: UnitValue;
  public factorOf: number;
  public price: UnitValue;
  public pricePer: UnitValue;
  public fromYear: number;
  public toYear: number;
  public toYearMax: boolean;
  public inputsErrors: {};

  constructor(param: PriceStructureInterface = {
    _id: null,
    name: '',
    uuid: null,
    cpi: new UnitValue(),
    rangeFrom: 0,
    rangeTo: null,
    rangeToMax: true,
    rangeUnit: new UnitValue(),
    rangePer: new UnitValue({value: 1, unit: ''}),
    factorOf: 1,
    price: new UnitValue(),
    pricePer: new UnitValue(),
    fromYear: 1,
    toYear: null,
    toYearMax: true,
    inputsErrors: {}
  }) {
    if (param._id) {
      this._id = param._id;
    } else {
      delete this._id;
    }
    this.name = param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.cpi = new UnitValue((param.cpi));
    this.rangeFrom = param.rangeFrom;
    this.rangeTo = param.rangeTo;
    this.rangeToMax = param.rangeToMax;
    this.rangeUnit = new UnitValue((param.rangeUnit));
    this.rangePer = new UnitValue((param.rangePer));
    this.factorOf = param.factorOf;
    this.price = new UnitValue((param.price));
    this.pricePer = new UnitValue((param.pricePer));
    this.fromYear = param.fromYear;
    this.toYear = param.toYear;
    this.toYearMax = param.toYearMax;
    this.inputsErrors = param.inputsErrors ? JSON.parse(JSON.stringify(param.inputsErrors)) : {};
  }

  getSummaryInputs(path: string[]): InputSummaryItem[] {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.cpi, this.cpi, [...path, 'cpi']);
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.rangeFrom, this.rangeFrom, [...path, 'rangeFrom']);
    inputs = InputSummaryUtils.performPush(
      inputs,
      PriceStructureSummaryProperties.rangeTo,
      this.rangeTo ? this.rangeTo : 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.THE_REST',
      [...path, 'rangeTo']
    );
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.rangeUnit, this.rangeUnit, [...path, 'rangeUnit']);
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.rangePer, this.rangePer, [...path, 'rangePer']);
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.factorOf, this.factorOf, [...path, 'factorOf']);
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.price, this.price, [...path, 'price']);
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.pricePer, this.pricePer, [...path, 'pricePer']);
    inputs = InputSummaryUtils.performPush(inputs, PriceStructureSummaryProperties.fromYear, this.fromYear, [...path, 'fromYear']);
    inputs = InputSummaryUtils.performPush(
      inputs,
      PriceStructureSummaryProperties.toYear,
      this.toYear ? this.toYear : 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.FINAL',
      [...path, 'toYear']
    );
    return inputs;
  }

  getValidation(): ProjectValidationUnit {
    const validation = new ProjectValidationUnit();
    for (const propKey of Object.keys(PriceStructureSummaryProperties)) {
      if (this[propKey] instanceof UnitValue) {
        validation.addModuleResults(
          new UnitValueValidation(this[propKey]).getValidation(),
          PriceStructureSummaryProperties[propKey]
        );
      } else {
        let valid = false;
        if (propKey === 'rangeTo' || propKey === 'toYear') {
          valid = this.isValid(this[propKey]) || this.isValid(this[propKey + 'Max']);
        } else {
          valid = this.isValid(this[propKey]);
        }
        validation.validate(valid, PriceStructureSummaryProperties[propKey]);
      }
    }
    for (const inputKey of Object.keys(this.inputsErrors)) {
      const error = this.inputsErrors[inputKey];
      if (error) { validation.setInvalid(PriceStructureSummaryProperties[inputKey], [error]); }
    }
    return validation;
  }

  isValid(value): boolean {
    return Boolean(value || value === 0);
  }

}

export enum PriceStructureRangeOf {
  gross = 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.GROSS_PRODUCT',
  sold = 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.SOLD_PRODUCT',
  installed = 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.INSTALLED'
}

export enum PriceStructureRangeOfTooltips {
  gross = 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.GROSS_PRODUCT_TOOLTIP',
  sold = 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.SOLD_PRODUCT_TOOLTIP',
  installed = 'PROJECT.FINANCIAL_GEN_PROD.PRICE_STRUCTURE.INSTALLED_TOOLTIP'
}

export enum PriceStructureSummaryProperties {
  cpi = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.CPI',
  rangeFrom = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.RANGE_FROM',
  rangeTo = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.RANGE_TO',
  rangeUnit = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.RANGE_UNIT',
  rangePer = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.RANGE_PER',
  factorOf = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.FACTOR_OF',
  price = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.PRICE',
  pricePer = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.PRICE_PER',
  fromYear = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.FROM_YEAR',
  toYear = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE_STRUCTURE.TO_YEAR',
}

